import React, { useState, useEffect } from 'react';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { financialDataService } from '../../services';
import type { DetailedFinancialData } from '../../types/market';

interface DetailedFinancialStatementsProps {
  symbol: string;
}

type TabType = 'balance' | 'income' | 'cashflow' | 'earnings';

const FINANCIAL_TABS = [
  { id: 'balance', label: 'Balance Sheet', icon: '⚖️' },
  { id: 'income', label: 'Income Statement', icon: '📊' },
  { id: 'cashflow', label: 'Cash Flow', icon: '💰' },
  { id: 'earnings', label: 'Earnings & Dividends', icon: '📈' },
] as const;

export const DetailedFinancialStatements: React.FC<DetailedFinancialStatementsProps> = ({ symbol }) => {
  const [financialData, setFinancialData] = useState<DetailedFinancialData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('balance');

  useEffect(() => {
    const fetchDetailedFinancialData = async () => {
      try {
        console.log('🔍 Fetching detailed financial data for symbol:', symbol);
        setLoading(true);
        setError(null);
        
        const data = await financialDataService.instance.getDetailedFinancialData(symbol);
        
        console.log('📊 Detailed financial data received:', data);
        setFinancialData(data);
      } catch (err) {
        console.error('❌ Error fetching detailed financial data:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch financial data';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (symbol) {
      fetchDetailedFinancialData();
    }
  }, [symbol]);

  const formatCurrency = (value: number): string => {
    if (value === 0) return '-';
    
    const absValue = Math.abs(value);
    if (absValue >= 1e12) {
      return `$${(value / 1e12).toFixed(2)}T`;
    } else if (absValue >= 1e9) {
      return `$${(value / 1e9).toFixed(2)}B`;
    } else if (absValue >= 1e6) {
      return `$${(value / 1e6).toFixed(2)}M`;
    } else if (absValue >= 1e3) {
      return `$${(value / 1e3).toFixed(2)}K`;
    } else {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    }
  };

  const formatNumber = (value: number, decimals: number = 2): string => {
    if (value === 0) return '-';
    
    const absValue = Math.abs(value);
    if (absValue >= 1e9) {
      return `${(value / 1e9).toFixed(decimals)}B`;
    } else if (absValue >= 1e6) {
      return `${(value / 1e6).toFixed(decimals)}M`;
    } else if (absValue >= 1e3) {
      return `${(value / 1e3).toFixed(decimals)}K`;
    } else {
      return value.toFixed(decimals);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" text="Loading detailed financial data..." />
        </div>
      </div>
    );
  }

  if (error || !financialData) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-yellow-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Financial Data Not Available</h3>
            <p className="text-gray-600 mb-4">
              {error?.includes('not available') 
                ? `Detailed financial statements for ${symbol} are not currently available.`
                : 'We are unable to retrieve financial data at this time. Please try again later.'
              }
            </p>
          </div>
        </div>
      </div>
    );
  }

  const renderBalanceSheet = () => (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full border border-gray-200">
          <thead>
            <tr className="bg-gray-50">
              <th className="text-left py-3 px-4 font-semibold text-gray-900 border-b border-gray-200">
                Metric
              </th>
              <th className="text-right py-3 px-4 font-semibold text-gray-900 border-b border-gray-200">
                3M (Restated)
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Total Assets</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.balanceSheet.totalAssets)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Current Assets</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.balanceSheet.currentAssets)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Total Non-Current Assets</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.balanceSheet.totalNonCurrentAssets)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Cash and Cash Equivalents</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.balanceSheet.cashAndCashEquivalents)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Accounts Receivable</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.balanceSheet.accountsReceivable)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Other Short-Term Investments</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.balanceSheet.otherShortTermInvestments)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Net Property, Plant, and Equipment</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.balanceSheet.netPropertyPlantEquipment)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Goodwill</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.balanceSheet.goodwill)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Total Liabilities</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.balanceSheet.totalLiabilities)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Current Liabilities</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.balanceSheet.currentLiabilities)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Long-Term Debt</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.balanceSheet.longTermDebt)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Accounts Payable</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.balanceSheet.accountsPayable)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Stockholders' Equity</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.balanceSheet.stockholdersEquity)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Common Stock</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.balanceSheet.commonStock)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Retained Earnings</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.balanceSheet.retainedEarnings)}</td>
            </tr>
            <tr>
              <td className="py-3 px-4 font-medium text-gray-900">Working Capital</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.balanceSheet.workingCapital)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-sm text-gray-600 mt-4">
        <strong>Note</strong>: Balance Sheet data is only provided for the 3M period in the JSON.
      </p>
    </div>
  );

  const renderIncomeStatement = () => (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full border border-gray-200">
          <thead>
            <tr className="bg-gray-50">
              <th className="text-left py-3 px-4 font-semibold text-gray-900 border-b border-gray-200">
                Metric
              </th>
              <th className="text-right py-3 px-4 font-semibold text-gray-900 border-b border-gray-200">
                3M (Restated)
              </th>
              <th className="text-right py-3 px-4 font-semibold text-gray-900 border-b border-gray-200">
                6M (Restated)
              </th>
              <th className="text-right py-3 px-4 font-semibold text-gray-900 border-b border-gray-200">
                12M (Restated)
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Total Revenue</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['3M'].totalRevenue)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['6M'].totalRevenue)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['12M'].totalRevenue)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Operating Revenue</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['3M'].operatingRevenue)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['6M'].operatingRevenue)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['12M'].operatingRevenue)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Cost of Revenue</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['3M'].costOfRevenue)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['6M'].costOfRevenue)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['12M'].costOfRevenue)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Gross Profit</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['3M'].grossProfit)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['6M'].grossProfit)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['12M'].grossProfit)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Operating Expense</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['3M'].operatingExpense)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['6M'].operatingExpense)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['12M'].operatingExpense)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Operating Income</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['3M'].operatingIncome)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['6M'].operatingIncome)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['12M'].operatingIncome)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Net Income</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['3M'].netIncome)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['6M'].netIncome)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['12M'].netIncome)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">EBITDA</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['3M'].ebitda)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['6M'].ebitda)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['12M'].ebitda)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Pretax Income</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['3M'].pretaxIncome)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['6M'].pretaxIncome)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['12M'].pretaxIncome)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Tax Provision</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['3M'].taxProvision)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['6M'].taxProvision)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['12M'].taxProvision)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Research and Development</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['3M'].researchAndDevelopment)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['6M'].researchAndDevelopment)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['12M'].researchAndDevelopment)}</td>
            </tr>
            <tr>
              <td className="py-3 px-4 font-medium text-gray-900">Selling, General, and Admin</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['3M'].sellingGeneralAdmin)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['6M'].sellingGeneralAdmin)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.incomeStatement.periods['12M'].sellingGeneralAdmin)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderCashFlowStatement = () => (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full border border-gray-200">
          <thead>
            <tr className="bg-gray-50">
              <th className="text-left py-3 px-4 font-semibold text-gray-900 border-b border-gray-200">
                Metric
              </th>
              <th className="text-right py-3 px-4 font-semibold text-gray-900 border-b border-gray-200">
                3M (Restated)
              </th>
              <th className="text-right py-3 px-4 font-semibold text-gray-900 border-b border-gray-200">
                6M (Restated)
              </th>
              <th className="text-right py-3 px-4 font-semibold text-gray-900 border-b border-gray-200">
                12M (Restated)
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Operating Cash Flow</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['3M'].operatingCashFlow)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['6M'].operatingCashFlow)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['12M'].operatingCashFlow)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Investing Cash Flow</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['3M'].investingCashFlow)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['6M'].investingCashFlow)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['12M'].investingCashFlow)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Financing Cash Flow</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['3M'].financingCashFlow)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['6M'].financingCashFlow)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['12M'].financingCashFlow)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Free Cash Flow</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['3M'].freeCashFlow)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['6M'].freeCashFlow)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['12M'].freeCashFlow)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Capital Expenditure</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['3M'].capitalExpenditure)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['6M'].capitalExpenditure)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['12M'].capitalExpenditure)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Cash Dividends Paid</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['3M'].cashDividendsPaid)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['6M'].cashDividendsPaid)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['12M'].cashDividendsPaid)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Net Income from Continuing Operations</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['3M'].netIncomeFromContinuingOperations)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['6M'].netIncomeFromContinuingOperations)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['12M'].netIncomeFromContinuingOperations)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Depreciation and Amortization</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['3M'].depreciationAndAmortization)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['6M'].depreciationAndAmortization)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['12M'].depreciationAndAmortization)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Change in Working Capital</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['3M'].changeInWorkingCapital)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['6M'].changeInWorkingCapital)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['12M'].changeInWorkingCapital)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Beginning Cash Position</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['3M'].beginningCashPosition)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['6M'].beginningCashPosition)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['12M'].beginningCashPosition)}</td>
            </tr>
            <tr>
              <td className="py-3 px-4 font-medium text-gray-900">End Cash Position</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['3M'].endCashPosition)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['6M'].endCashPosition)}</td>
              <td className="text-right py-3 px-4">{formatCurrency(financialData.cashFlowStatement.periods['12M'].endCashPosition)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderEarningsData = () => (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full border border-gray-200">
          <thead>
            <tr className="bg-gray-50">
              <th className="text-left py-3 px-4 font-semibold text-gray-900 border-b border-gray-200">
                Metric
              </th>
              <th className="text-right py-3 px-4 font-semibold text-gray-900 border-b border-gray-200">
                3M (Restated)
              </th>
              <th className="text-right py-3 px-4 font-semibold text-gray-900 border-b border-gray-200">
                6M (Restated)
              </th>
              <th className="text-right py-3 px-4 font-semibold text-gray-900 border-b border-gray-200">
                12M (Restated)
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Basic EPS</td>
              <td className="text-right py-3 px-4">${financialData.earningsData.periods['3M'].basicEPS.toFixed(2)}</td>
              <td className="text-right py-3 px-4">${financialData.earningsData.periods['6M'].basicEPS.toFixed(2)}</td>
              <td className="text-right py-3 px-4">${financialData.earningsData.periods['12M'].basicEPS.toFixed(2)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Diluted EPS</td>
              <td className="text-right py-3 px-4">${financialData.earningsData.periods['3M'].dilutedEPS.toFixed(2)}</td>
              <td className="text-right py-3 px-4">${financialData.earningsData.periods['6M'].dilutedEPS.toFixed(2)}</td>
              <td className="text-right py-3 px-4">${financialData.earningsData.periods['12M'].dilutedEPS.toFixed(2)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Basic Average Shares</td>
              <td className="text-right py-3 px-4">{formatNumber(financialData.earningsData.periods['3M'].basicAverageShares)}</td>
              <td className="text-right py-3 px-4">{formatNumber(financialData.earningsData.periods['6M'].basicAverageShares)}</td>
              <td className="text-right py-3 px-4">{formatNumber(financialData.earningsData.periods['12M'].basicAverageShares)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Diluted Average Shares</td>
              <td className="text-right py-3 px-4">{formatNumber(financialData.earningsData.periods['3M'].dilutedAverageShares)}</td>
              <td className="text-right py-3 px-4">{formatNumber(financialData.earningsData.periods['6M'].dilutedAverageShares)}</td>
              <td className="text-right py-3 px-4">{formatNumber(financialData.earningsData.periods['12M'].dilutedAverageShares)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 font-medium text-gray-900">Dividend Per Share</td>
              <td className="text-right py-3 px-4">${financialData.earningsData.periods['3M'].dividendPerShare.toFixed(2)}</td>
              <td className="text-right py-3 px-4">${financialData.earningsData.periods['6M'].dividendPerShare.toFixed(2)}</td>
              <td className="text-right py-3 px-4">${financialData.earningsData.periods['12M'].dividendPerShare.toFixed(2)}</td>
            </tr>
            <tr>
              <td className="py-3 px-4 font-medium text-gray-900">Dividend Coverage Ratio</td>
              <td className="text-right py-3 px-4">{financialData.earningsData.periods['3M'].dividendCoverageRatio.toFixed(2)}</td>
              <td className="text-right py-3 px-4">{financialData.earningsData.periods['6M'].dividendCoverageRatio.toFixed(2)}</td>
              <td className="text-right py-3 px-4">{financialData.earningsData.periods['12M'].dividendCoverageRatio.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderActiveTabContent = () => {
    switch (activeTab) {
      case 'balance':
        return renderBalanceSheet();
      case 'income':
        return renderIncomeStatement();
      case 'cashflow':
        return renderCashFlowStatement();
      case 'earnings':
        return renderEarningsData();
      default:
        return renderBalanceSheet();
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">
          Financial Statements - {symbol}
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Comprehensive financial data from Tradier API
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 px-6" aria-label="Tabs">
          {FINANCIAL_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {renderActiveTabContent()}
      </div>
    </div>
  );
};